import { useState, useEffect, lazy, Suspense, type FormEvent } from 'react';
import {
  useTask,
  useSubtasks,
  useUpdateTask,
  useDeleteTask,
  useCreateTask,
  useProjects,
} from '@/hooks/useTasks.ts';
import { toDateTimeLocal, fromDateTimeLocal, formatDue } from '@/lib/dates.ts';
/**
 * TipTap is roughly half the application's JavaScript and is only needed once
 * a task is actually opened, so it is fetched on demand rather than charged to
 * every page load — which matters most on a phone over the tunnel.
 */
const NotesEditor = lazy(() =>
  import('./NotesEditor.tsx').then((m) => ({ default: m.NotesEditor })),
);
import { TASK_STATUSES, PRIORITIES, PRIORITY_LABELS } from '@shared/types';
import { RECUR_FREQS, RECUR_LABELS, describeRecurrence } from '@shared/recurrence';
import type { Priority, TaskStatus, UpdateTaskInput } from '@shared/types';

const STATUS_LABELS: Record<TaskStatus, string> = {
  inbox: 'Inbox',
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

/**
 * Task detail as a right-hand drawer rather than a route change, so the list
 * behind it keeps its scroll position. The open task lives in the URL
 * (`?task=<id>`) so it survives a refresh and the back button closes it.
 */
export function TaskDrawer({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const { data: task, isPending, isError } = useTask(taskId);
  const { data: subtasks } = useSubtasks(taskId);
  const { data: projects } = useProjects();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const createTask = useCreateTask();

  const [title, setTitle] = useState('');
  const [newSubtask, setNewSubtask] = useState('');
  const [tagText, setTagText] = useState('');

  // Seed the editable fields when a different task is opened.
  //
  // Keyed on id alone, deliberately. Depending on the task's contents would
  // re-run on every background refetch and overwrite whatever is half-typed in
  // the title box. Notes are owned by the editor, which is keyed the same way.
  useEffect(() => {
    if (!task) return;
    setTitle(task.title);
    setTagText(task.tags.map((t) => t.name).join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function save(patch: UpdateTaskInput) {
    updateTask.mutate({ id: taskId, ...patch });
  }

  function commitTags() {
    const names = tagText
      .split(',')
      .map((t) => t.trim().replace(/^#/, ''))
      .filter(Boolean);
    save({ tag_names: names, tag_ids: [] });
  }

  function addSubtask(e: FormEvent) {
    e.preventDefault();
    const trimmed = newSubtask.trim();
    if (!trimmed) return;
    createTask.mutate(
      { title: trimmed, parent_id: taskId, status: 'todo' },
      { onSuccess: () => setNewSubtask('') },
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-label="Task details"
        className="fixed inset-y-0 right-0 z-50 w-full max-w-md overflow-y-auto p-5"
        style={{ background: 'var(--color-page)', borderLeft: '1px solid var(--color-line)' }}
      >
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="text-sm"
            style={{ color: 'var(--color-ink-2)' }}
          >
            Close
          </button>
          {task && (
            <button
              type="button"
              onClick={() => {
                if (confirm(`Delete "${task.title}"? This cannot be undone.`)) {
                  deleteTask.mutate(taskId, { onSuccess: onClose });
                }
              }}
              className="text-sm"
              style={{ color: 'var(--color-brick)' }}
            >
              Delete
            </button>
          )}
        </div>

        {isPending && <p style={{ color: 'var(--color-ink-2)' }}>Loading…</p>}
        {isError && <p style={{ color: 'var(--color-brick)' }}>Could not load this task.</p>}

        {task && (
          <div className="space-y-4">
            <textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => title.trim() && title !== task.title && save({ title })}
              rows={2}
              aria-label="Title"
              className="w-full resize-none rounded-md px-2 py-1.5 text-base font-medium outline-none focus:ring-2"
              style={{ background: 'transparent', color: 'var(--color-ink)' }}
            />

            <Field label="Status">
              <Select
                value={task.status}
                onChange={(v) => save({ status: v as TaskStatus })}
                options={TASK_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
              />
            </Field>

            <Field label="Priority">
              <Select
                value={String(task.priority)}
                onChange={(v) => save({ priority: Number(v) as Priority })}
                options={PRIORITIES.map((p) => ({ value: String(p), label: PRIORITY_LABELS[p] }))}
              />
            </Field>

            <Field label="Due">
              <input
                type="datetime-local"
                value={toDateTimeLocal(task.due_at)}
                onChange={(e) => save({ due_at: fromDateTimeLocal(e.target.value) })}
                className="w-full rounded-md px-2 py-1.5 outline-none focus:ring-2"
                style={{
                  background: 'var(--color-inset)',
                  border: '1px solid var(--color-line)',
                  color: 'var(--color-ink)',
                }}
              />
              {task.due_at && (
                <p className="mt-1 text-xs" style={{ color: 'var(--color-ink-2)' }}>
                  {formatDue(task.due_at)}
                </p>
              )}
            </Field>

            <Field label="Project">
              <Select
                value={task.project_id ?? ''}
                onChange={(v) => save({ project_id: v || null })}
                options={[
                  { value: '', label: 'No project' },
                  ...(projects ?? []).map((p) => ({ value: p.id, label: p.name })),
                ]}
              />
            </Field>

            <Field label="Repeats">
              <Select
                value={task.recur_freq ?? ''}
                onChange={(v) =>
                  save({ recur_freq: (v || null) as UpdateTaskInput['recur_freq'] })
                }
                options={[
                  { value: '', label: 'Does not repeat' },
                  ...RECUR_FREQS.map((f) => ({ value: f, label: RECUR_LABELS[f] })),
                ]}
              />

              {task.recur_freq && (
                <div className="mt-2 space-y-2">
                  {task.recur_freq !== 'weekdays' && (
                    <label className="flex items-center gap-2 text-xs">
                      <span style={{ color: 'var(--color-ink-2)' }}>Every</span>
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={task.recur_interval}
                        onChange={(e) =>
                          save({ recur_interval: Math.max(1, Number(e.target.value) || 1) })
                        }
                        className="w-16 rounded px-1.5 py-1 outline-none focus:ring-2"
                        style={{
                          background: 'var(--color-inset)',
                          border: '1px solid var(--color-line)',
                          color: 'var(--color-ink)',
                        }}
                      />
                      <span style={{ color: 'var(--color-ink-2)' }}>
                        {task.recur_freq === 'daily'
                          ? 'days'
                          : task.recur_freq === 'weekly'
                            ? 'weeks'
                            : 'months'}
                      </span>
                    </label>
                  )}

                  <Select
                    value={task.recur_from}
                    onChange={(v) => save({ recur_from: v as UpdateTaskInput['recur_from'] })}
                    options={[
                      { value: 'due', label: 'Next date counts from the due date' },
                      { value: 'completion', label: 'Next date counts from when I finish it' },
                    ]}
                  />

                  <p className="text-xs" style={{ color: 'var(--color-ink-2)' }}>
                    {task.recur_from === 'due'
                      ? 'Stays on schedule even if you finish late.'
                      : 'Good for habits — the clock restarts when you tick it off.'}
                    {' '}
                    {describeRecurrence({
                      freq: task.recur_freq,
                      interval: task.recur_interval,
                      from: task.recur_from,
                    })}
                    .
                  </p>

                  {task.recur_from === 'due' && !task.due_at && (
                    <p role="alert" className="text-xs" style={{ color: 'var(--color-brick)' }}>
                      Set a due date, or this will not repeat — there is nothing to count from.
                    </p>
                  )}
                </div>
              )}
            </Field>

            <Field label="Tags">
              <input
                value={tagText}
                onChange={(e) => setTagText(e.target.value)}
                onBlur={commitTags}
                placeholder="close, urgent"
                className="w-full rounded-md px-2 py-1.5 outline-none focus:ring-2"
                style={{
                  background: 'var(--color-inset)',
                  border: '1px solid var(--color-line)',
                  color: 'var(--color-ink)',
                }}
              />
              <p className="mt-1 text-xs" style={{ color: 'var(--color-ink-2)' }}>
                Comma separated. New tags are created automatically.
              </p>
            </Field>

            <Field label="Notes">
              {/* Keyed on the task id so switching tasks rebuilds the editor;
                  TipTap holds its own document state and would otherwise keep
                  showing the previous task's notes. */}
              <Suspense
                fallback={
                  <div
                    className="rounded-md px-2 py-1.5 text-sm"
                    style={{
                      background: 'var(--color-inset)',
                      border: '1px solid var(--color-line)',
                      color: 'var(--color-ink-2)',
                      minHeight: '8rem',
                    }}
                  >
                    {task.notes_text || 'Loading editor…'}
                  </div>
                }
              >
                <NotesEditor
                  key={task.id}
                  notesJson={task.notes_json}
                  notesText={task.notes_text}
                  onSave={(notes_json, notes_text) => save({ notes_json, notes_text })}
                />
              </Suspense>
            </Field>

            {/* Subtasks are only offered on top-level tasks — the schema allows
                exactly one level, so a subtask cannot have children. */}
            {task.parent_id === null && (
              <Field label={`Subtasks${subtasks?.length ? ` (${task.subtask_done_count}/${task.subtask_count})` : ''}`}>
                <ul className="mb-2">
                  {(subtasks ?? []).map((sub) => (
                    <li key={sub.id} className="flex items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        checked={sub.status === 'done'}
                        onChange={() =>
                          updateTask.mutate({
                            id: sub.id,
                            status: sub.status === 'done' ? 'todo' : 'done',
                          })
                        }
                        className="size-4 shrink-0"
                        style={{ accentColor: 'var(--color-brass)' }}
                      />
                      <span
                        className="text-sm"
                        style={{
                          color: sub.status === 'done' ? 'var(--color-ink-2)' : 'var(--color-ink)',
                          textDecoration: sub.status === 'done' ? 'line-through' : 'none',
                        }}
                      >
                        {sub.title}
                      </span>
                    </li>
                  ))}
                </ul>
                <form onSubmit={addSubtask}>
                  <input
                    value={newSubtask}
                    onChange={(e) => setNewSubtask(e.target.value)}
                    placeholder="Add a subtask…"
                    className="w-full rounded-md px-2 py-1.5 text-sm outline-none focus:ring-2"
                    style={{
                      background: 'var(--color-inset)',
                      border: '1px solid var(--color-line)',
                      color: 'var(--color-ink)',
                    }}
                  />
                </form>
              </Field>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span
        className="mb-1 block text-xs font-medium uppercase tracking-wide"
        style={{ color: 'var(--color-ink-2)' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md px-2 py-1.5 outline-none focus:ring-2"
      style={{
        background: 'var(--color-inset)',
        border: '1px solid var(--color-line)',
        color: 'var(--color-ink)',
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
